// OCR.space API Service
// Free tier: 1000 requests/month — https://ocr.space/ocrapi
//
// This module is split in three layers:
//   1. ICAO 9303 MRZ parsers (TD1 / TD2 / TD3) — machine-readable zone
//   2. International text-label parsers — multi-language label/value extraction
//   3. Document type detection + main entry parseDocumentOCR
//
// The OCR provider call is routed through the backend `/api/ocr/document`
// proxy to avoid CORS issues and keep the API key server-side.

import { api } from './api';
import { parse as parseMrzLibrary } from 'mrz';

const OCR_SPACE_API_KEY = process.env.REACT_APP_OCR_SPACE_API_KEY;

// ============================================================================
// PUBLIC TYPES
// ============================================================================

export interface ParsedIDData {
  nom: string;
  prenom: string;
  date_naissance: string;   // ISO YYYY-MM-DD
  lieu_naissance: string;
  numero_id: string;
  nationality: string;      // ISO 3-letter when known, else raw text
  expiry_date: string;      // ISO YYYY-MM-DD
  detected_doc_type: 'passport' | 'identity_card' | 'driving_license' | 'other' | '';
}

// ============================================================================
// 1. DATE PARSING
// ============================================================================

const MONTH_LOOKUP: Record<string, string> = {
  // English
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
  // French
  janv: '01', fevr: '02', mars: '03', avri: '04', juin: '06',
  juil: '07', aout: '08', octo: '10', nove: '11', dece: '12',
  // Spanish abbreviations (3 letters)
  ene: '01', abr: '04', ago: '08', dic: '12',
};

/**
 * Parse a date fragment into ISO YYYY-MM-DD. Tries several international formats.
 */
const parseDate = (text: string): string => {
  if (!text) return '';
  const cleaned = text.trim();

  // 1. DD MMM YYYY  e.g. "07 Aug 1999", "14 jul 1977"
  const monthMatch = cleaned.match(/\b(\d{1,2})\s+([A-Za-zàâäéèêëïîôùûüç]{3,12})\s+(\d{4})\b/);
  if (monthMatch) {
    const norm = monthMatch[2].toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const mm = MONTH_LOOKUP[norm.slice(0, 4)] || MONTH_LOOKUP[norm.slice(0, 3)];
    if (mm) {
      const d = parseInt(monthMatch[1], 10);
      const y = parseInt(monthMatch[3], 10);
      if (d >= 1 && d <= 31 && y >= 1900 && y <= 2099) {
        return `${y}-${mm}-${String(d).padStart(2, '0')}`;
      }
    }
  }

  // 2. YYYY-MM-DD (already ISO-ish)
  const isoMatch = cleaned.match(/\b(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})\b/);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10);
    const d = parseInt(isoMatch[3], 10);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2099) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  // 3. DD-MM-YYYY / DD/MM/YYYY / DD.MM.YYYY (most international)
  const sepMatch = cleaned.match(/\b(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})\b/);
  if (sepMatch) {
    const d = parseInt(sepMatch[1], 10);
    const m = parseInt(sepMatch[2], 10);
    const y = parseInt(sepMatch[3], 10);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2099) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  // 4. DD MM YYYY (space-separated, French CIN style)
  const spaceMatch = cleaned.match(/\b(\d{1,2})\s+(\d{1,2})\s+(\d{4})\b/);
  if (spaceMatch) {
    const d = parseInt(spaceMatch[1], 10);
    const m = parseInt(spaceMatch[2], 10);
    const y = parseInt(spaceMatch[3], 10);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2099) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  // 5. DD MMYYYY (OCR-merged, e.g. "13 071990")
  const merged = cleaned.match(/\b(\d{1,2})\s+(\d{2})(\d{4})\b/);
  if (merged) {
    const d = parseInt(merged[1], 10);
    const m = parseInt(merged[2], 10);
    const y = parseInt(merged[3], 10);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2099) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  return '';
};

/** US-specific date parser for MM/DD/YYYY (used only in US DL parser). */
const parseUsDate = (text: string): string => {
  if (!text) return '';
  const m = text.trim().match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (!m) return '';
  const mm = parseInt(m[1], 10);
  const dd = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2099) return '';
  return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

/** Convert MRZ date YYMMDD → ISO YYYY-MM-DD. */
const parseMrzDate = (yymmdd: string): string => {
  if (!yymmdd || yymmdd.length !== 6 || /[^0-9]/.test(yymmdd)) return '';
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  const currentYY = new Date().getFullYear() % 100;
  // 2-digit-year disambiguation: years > current are treated as 19xx
  const yyyy = yy <= currentYY
    ? `20${String(yy).padStart(2, '0')}`
    : `19${String(yy).padStart(2, '0')}`;
  return `${yyyy}-${mm}-${dd}`;
};

// ============================================================================
// 2. NATIONALITY NORMALIZATION
// ============================================================================

/** Map of long-form nationalities to ICAO 3-letter codes. */
const NATIONALITY_MAP: Record<string, string> = {
  // English / common variants
  'BRITISH': 'GBR', 'BRITISH CITIZEN': 'GBR', 'UNITED KINGDOM': 'GBR',
  'UK': 'GBR', 'GREAT BRITAIN': 'GBR', 'ENGLAND': 'GBR', 'SCOTLAND': 'GBR',
  'AMERICAN': 'USA', 'UNITED STATES': 'USA', 'UNITED STATES OF AMERICA': 'USA',
  'US': 'USA', 'USA': 'USA',
  'FRENCH': 'FRA', 'FRANCE': 'FRA', 'FRANCAIS': 'FRA', 'FRANCAISE': 'FRA',
  'REPUBLIQUE FRANCAISE': 'FRA', 'RÉPUBLIQUE FRANÇAISE': 'FRA',
  'GERMAN': 'DEU', 'GERMANY': 'DEU', 'DEUTSCHLAND': 'DEU', 'DEUTSCHE': 'DEU',
  'SPANISH': 'ESP', 'SPAIN': 'ESP', 'ESPANA': 'ESP', 'ESPAÑA': 'ESP', 'ESPAÑOLA': 'ESP',
  'ITALIAN': 'ITA', 'ITALY': 'ITA', 'ITALIA': 'ITA', 'ITALIANA': 'ITA',
  'CANADIAN': 'CAN', 'CANADA': 'CAN',
  'AUSTRALIAN': 'AUS', 'AUSTRALIA': 'AUS',
  'IRISH': 'IRL', 'IRELAND': 'IRL',
  'NETHERLANDS': 'NLD', 'DUTCH': 'NLD', 'NEDERLAND': 'NLD',
  'BELGIAN': 'BEL', 'BELGIUM': 'BEL', 'BELGIQUE': 'BEL',
  'SWISS': 'CHE', 'SWITZERLAND': 'CHE', 'SUISSE': 'CHE',
  'PORTUGUESE': 'PRT', 'PORTUGAL': 'PRT',
};

const normalizeNationality = (raw: string): string => {
  if (!raw) return '';
  const cleaned = raw.toUpperCase().trim().replace(/[^\w\sÀ-ÿ]/g, '');
  if (/^[A-Z]{3}$/.test(cleaned)) return cleaned;
  return NATIONALITY_MAP[cleaned] || raw.trim();
};

// ============================================================================
// 3. ICAO 9303 MRZ PARSERS
// ============================================================================

const cleanMrzText = (s: string): string => s.replace(/</g, ' ').trim().replace(/\s+/g, ' ');

type MrzLibraryResult = {
  format?: string;
  fields?: {
    lastName?: string;
    firstName?: string;
    documentNumber?: string;
    nationality?: string | null;
    birthDate?: string;
    expirationDate?: string;
  };
};

const mapMrzFormatToDocType = (format?: string): ParsedIDData['detected_doc_type'] => {
  if (!format) return '';
  if (format === 'TD3') return 'passport';
  if (format === 'TD1' || format === 'TD2' || format === 'FRENCH_NATIONAL_ID') return 'identity_card';
  if (format.includes('DRIVING_LICENSE')) return 'driving_license';
  return '';
};

const parseWithMrzLibrary = (mrzLines: string[]): Partial<ParsedIDData> => {
  const parseCandidate = (candidate: string[]): Partial<ParsedIDData> => {
    try {
      const parsed = parseMrzLibrary(candidate) as MrzLibraryResult;
      if (!parsed?.fields) return {};
      const f = parsed.fields;
      const out: Partial<ParsedIDData> = {
        nom: (f.lastName || '').trim(),
        prenom: (f.firstName || '').trim(),
        numero_id: (f.documentNumber || '').trim(),
        nationality: normalizeNationality((f.nationality || '').trim()),
        date_naissance: parseMrzDate((f.birthDate || '').trim()),
        expiry_date: parseMrzDate((f.expirationDate || '').trim()),
        detected_doc_type: mapMrzFormatToDocType(parsed.format),
      };
      // Keep only non-empty values so fallback merge works naturally.
      Object.keys(out).forEach((k) => {
        const key = k as keyof ParsedIDData;
        if (!out[key]) delete (out as Partial<ParsedIDData>)[key];
      });
      return out;
    } catch {
      return {};
    }
  };

  // Try consecutive windows first (most common OCR result ordering).
  for (let i = 0; i < mrzLines.length; i++) {
    const two = mrzLines.slice(i, i + 2);
    if (two.length === 2) {
      const out = parseCandidate(two);
      if (Object.keys(out).length > 0) return out;
    }
    const three = mrzLines.slice(i, i + 3);
    if (three.length === 3) {
      const out = parseCandidate(three);
      if (Object.keys(out).length > 0) return out;
    }
  }

  // Last fallback: full set (handles some OCR reorderings).
  if (mrzLines.length >= 2) {
    return parseCandidate(mrzLines);
  }
  return {};
};

/** TD3 — passport: 2 lines × 44 chars. Line 1 starts P<. */
const parseTD3 = (l1: string, l2: string): Partial<ParsedIDData> => {
  const line1 = l1.padEnd(44, '<');
  const line2 = l2.padEnd(44, '<');
  const nat = line2.slice(10, 13).replace(/</g, '');

  // Line 1 layout: P< + issuing-country(3) + name
  // Some OCR drops the country code, yielding "P<DELAPAZ..."
  // We strip the first 3 letters only when they match the MRZ nationality code.
  const parts = line1.slice(2).split('<<');
  let surname = cleanMrzText(parts[0] || '').replace(/\s+/g, '');
  if (nat && surname.length > 3 && surname.slice(0, 3) === nat) {
    surname = surname.slice(3);
  }

  return {
    nom: surname,
    prenom: cleanMrzText((parts[1] || '').replace(/</g, ' ')),
    numero_id: line2.slice(0, 9).replace(/</g, ''),
    nationality: nat,
    date_naissance: parseMrzDate(line2.slice(13, 19)),
    expiry_date: parseMrzDate(line2.slice(21, 27)),
    detected_doc_type: 'passport',
  };
};

/** TD1 — ID card: 3 lines × 30 chars. Line 1 typically starts I, ID, IR, AR. */
const parseTD1 = (l1: string, l2: string, l3: string): Partial<ParsedIDData> => {
  const line2 = l2.padEnd(30, '<');
  const line3 = l3.padEnd(30, '<');
  const parts = line3.split('<<');

  return {
    nom: cleanMrzText(parts[0] || ''),
    prenom: cleanMrzText((parts[1] || '').replace(/</g, ' ')),
    numero_id: l1.slice(5, 14).replace(/</g, ''),
    nationality: line2.slice(15, 18).replace(/</g, ''),
    date_naissance: parseMrzDate(line2.slice(0, 6)),
    expiry_date: parseMrzDate(line2.slice(8, 14)),
    detected_doc_type: 'identity_card',
  };
};

/** TD2 — IDs / residence permits: 2 lines × 36 chars. */
const parseTD2 = (l1: string, l2: string): Partial<ParsedIDData> => {
  const line1 = l1.padEnd(36, '<');
  const line2 = l2.padEnd(36, '<');
  const parts = line1.slice(5).split('<<');

  return {
    nom: cleanMrzText(parts[0] || ''),
    prenom: cleanMrzText((parts[1] || '').replace(/</g, ' ')),
    numero_id: line2.slice(0, 9).replace(/</g, ''),
    nationality: line2.slice(10, 13).replace(/</g, ''),
    date_naissance: parseMrzDate(line2.slice(13, 19)),
    expiry_date: parseMrzDate(line2.slice(21, 27)),
    detected_doc_type: 'identity_card',
  };
};

/** Find candidate MRZ lines by stripping non-MRZ chars and applying density rules. */
const findMrzLines = (rawText: string): string[] => {
  const cleaned = rawText.split('\n').map((l) => {
    const stripped = l.trim().toUpperCase().replace(/\s/g, '').replace(/[^A-Z0-9<]/g, '');
    return stripped.length >= 20 ? stripped : '';
  });
  return cleaned.filter((l) => {
    if (l.length < 20) return false;
    // US driver license footer (document discriminator) — not ICAO MRZ
    if (!l.includes('<') && /^DD[0-9O]{6,}/i.test(l)) return false;
    if (!l.includes('<') && /(BIRTH|NAISS|NAISSANCE|EXPIR|EXPIR(?:Y|ATION)|SURNAME|GIVEN|NATIONALI|DATEDE|LIEU|PLACE|DOCUMENT|IDENTITY|CARTE)/i.test(l)) {
      // Concatenated bilingual VIZ (not ICAO MRZ) — e.g. "DATEDENAISSDATEOFBIRTH13071990"
      return false;
    }
    return l.includes('<') || /\d{6,}/.test(l);
  });
};

// ============================================================================
// 4. INTERNATIONAL LABEL-BASED FIELD EXTRACTION
// ============================================================================

type FieldKey =
  | 'surname' | 'given_names' | 'date_of_birth' | 'place_of_birth'
  | 'expiry_date' | 'issue_date' | 'nationality' | 'document_number';

/**
 * Multi-language label start patterns. Each pattern matches the *start* of a
 * label line (we let OCR junk follow until the colon separator). We support
 * the 4 most relevant languages for our user base: English, French, Spanish,
 * German — covering the bulk of EU + UK + US + Reunion documents.
 */
const FIELD_LABELS: Record<FieldKey, RegExp[]> = {
  surname: [
    /^surname\b/i,
    /^last\s+name\b/i,
    /^family\s+name\b/i,
    /^nom\b(?!\s*d[''])/i,                    // Skip "Nom d'usage" / "Nom d'épouse"
    /^apellidos?\b/i,
    /^familienname\b/i,
    /^sobrenome\b/i,
  ],
  given_names: [
    /^given\s+names?\b/i,
    /^first\s+name\b/i,
    /^forenames?\b/i,
    /^pr[ée]noms?\b/i,
    /^nombres?\b/i,
    /^vornamen?\b/i,
  ],
  date_of_birth: [
    /^date\s+of\s+birth\b/i,
    /^birth\s+date\b/i,
    /^d\.?o\.?b\.?\b/i,
    // French CIN: "DATE DE NAISS." (abbrev) or "DATE DE NAISSANCE"
    /^date\s+de\s+naiss(?:ance)?\.?/i,
    /^geburtsdatum\b/i,
    /^fecha\s+de\s+nacimiento\b/i,
    /^data\s+de\s+nascimento\b/i,
  ],
  place_of_birth: [
    /^place\s+of\s+birth\b/i,
    /^lieu\s+de\s+naissance\b/i,
    /^geburtsort\b/i,
    /^lugar\s+de\s+nacimiento\b/i,
    /^local\s+de\s+nascimento\b/i,
  ],
  expiry_date: [
    /^date\s+of\s+expir/i,                    // expiry, expiration
    /^expir(?:y|es?|ation)\s+date\b/i,
    /^valid\s+until\b/i,
    // French CIN: "DATE D'EXPIR." (abbrev) before "/ Expiry date"
    /^date\s+d['\u2019]?\s*expir/i,
    /^date\s+de\s+fin\b/i,
    /^g[üu]ltig\s+bis\b/i,
    /^fecha\s+de\s+caducidad\b/i,
  ],
  issue_date: [
    /^date\s+of\s+issue\b/i,
    /^issue\s+date\b/i,
    /^date\s+de\s+d[ée]livrance\b/i,
    /^ausstellungsdatum\b/i,
    /^fecha\s+de\s+expedici/i,
  ],
  nationality: [
    /^nationality\b/i,
    /^nationalit[ée]\b/i,
    /^staatsangeh[öo]rigkeit\b/i,
    /^nacionalidad\b/i,
    /^cidadania\b/i,
  ],
  document_number: [
    /^document\s+(?:no|number)\b/i,
    /^passport\s+(?:no|number)\b/i,
    /^driver(?:'s)?\s+licen[cs]e\s+(?:no|number)\b/i,
    /^licen[cs]e\s+(?:no|number)\b/i,
    /^id\s+(?:no|number)\b/i,
    /^n[°º']?\s*du\s+document\b/i,
    /^n[°º']?\s*du\s+passeport\b/i,
    /^n[°º']?\s*de\s+la\s+carte\b/i,
    /^n[°º']?\s*de\s+permis\b/i,
    /^n[uú]mero\s+de\s+documento\b/i,
  ],
};

/**
 * After the primary (often French) label, strip the bilingual " / English…"
 * segment. French 2021+ CIN (and similar documents) use:
 *   NOM / Surname MARTIN
 *   Prénoms / Given names …
 * with no colon — same-line value must be taken after the English sub-label.
 */
const BILINGUAL_SECOND_LABEL: Partial<Record<FieldKey, RegExp>> = {
  surname: /^\s*\/\s*(?:Surname|Last\s+name|Family\s+name)\b\.?\s*/i,
  given_names: /^\s*\/\s*(?:Given\s+names?|First\s+name|Forenames?)\b\.?\s*/i,
  date_of_birth: /^\s*\/\s*(?:Date\s+of\s+birth|Birth\s+date|Date\s+de\s+naissance)\b\.?\s*/i,
  place_of_birth: /^\s*\/\s*(?:Place\s+of\s+birth|Birth\s+place|Lieu\s+de\s+naissance)\b\.?\s*/i,
  expiry_date: /^\s*\/\s*(?:Expiry\s+date|Date\s+of\s+expir\w*|Date\s+d['\u2019]?\s*expir\w*)\b\.?\s*/i,
  nationality: /^\s*\/\s*(?:Nationality|Nationalit[ée])\b\.?\s*/i,
  document_number: /^\s*\/\s*(?:Document\s+No\.?|Document\s+number|N[°º']?\s*du\s+document)\b\.?\s*/i,
  issue_date: /^\s*\/\s*(?:Date\s+of\s+issue|Issue\s+date|Date\s+de\s+d[ée]livrance)\b\.?\s*/i,
};

/** Avoid `^`+pat.source if pat already has a leading `^` (double-anchor bug). */
const rxAnchorBody = (pat: RegExp): string => pat.source.replace(/^\^/, '');

/** Same line as label, bilingual FR+EN, no colon (e.g. CIN 2021). */
const extractSameLineLabelValue = (line: string, fieldKey: FieldKey): string | null => {
  const lineTrim = line.trim();
  for (const pat of FIELD_LABELS[fieldKey]) {
    const m = lineTrim.match(new RegExp('^(?:' + rxAnchorBody(pat) + ')(.*)$', pat.flags));
    if (!m) continue;
    let rest = (m[1] || '').trim();
    if (!rest) continue;
    if (rest.startsWith('.')) rest = rest.slice(1).trim();
    const strip = BILINGUAL_SECOND_LABEL[fieldKey];
    if (strip) {
      rest = rest.replace(strip, '').trim();
    }
    if (rest && !isLikelyLabelOnlyText(rest)) return rest;
  }
  return null;
};

/**
 * Try to extract the value when label and value are on the same line.
 *   "Surname: HENDERSON"               → "HENDERSON"
 *   "Surname/Hame: Henderson"          → "Henderson"   (OCR garbled label)
 *   "Date of expiry/validance: 2019…"  → "2019…"
 * Returns null if there's no inline value (i.e. line is label-only).
 */
const extractInlineValue = (line: string, fieldKey: FieldKey): string | null => {
  for (const pat of FIELD_LABELS[fieldKey]) {
    const source = pat.source.replace(/\\b$/, '');
    // Allow extra label/junk text after core label, then require ":" separator
    const stripPat = new RegExp(source + '[^:\\n]*:\\s*(.+)', pat.flags);
    const m = line.match(stripPat);
    if (m && m[1]) {
      const value = m[1].trim();
      // Reject values starting with separator junk
      if (value && !/^[\(\)\s\/\|]/.test(value)) return value;
    }
  }
  return null;
};

/**
 * True when the line is only a field header and the value is on the *next* line
 * (no same-line value after stripping bilingual " / Surname" etc.).
 */
const isLabelOnly = (line: string, fieldKey: FieldKey): boolean => {
  if (!line.trim() || line.includes(':')) return false;
  for (const pat of FIELD_LABELS[fieldKey]) {
    const m = line.trim().match(new RegExp('^(?:' + rxAnchorBody(pat) + ')(.*)$', pat.flags));
    if (!m) continue;
    let rest0 = (m[1] || '').trim();
    if (rest0.startsWith('.')) rest0 = rest0.slice(1).trim();
    if (!rest0) return true;
    const strip = BILINGUAL_SECOND_LABEL[fieldKey];
    if (strip) {
      const after = rest0.replace(strip, '').trim();
      if (after) return false;                 // e.g. "NOM / Surname MARTIN" → value on same line
      return true;                            // e.g. "NOM / Surname" with nothing more → value on next line
    }
    return false;
  }
  return false;
};

const NON_DATA_LINE = /^(?:SEXE|SEX|Height|Poids|Taille)\b|^(?:Signature|Signatur|Empreint)\b|^\d{5,6}$/i;

/** Returns true if the line looks like a field label (any field). */
const isAnyLabel = (line: string): boolean => {
  if (NON_DATA_LINE.test(line.trim())) return true;
  return (Object.keys(FIELD_LABELS) as FieldKey[]).some(
    (k) => isLabelOnly(line, k) || extractSameLineLabelValue(line, k) != null,
  );
};

/**
 * Extract the value for `fieldKey`. Tries, in order:
 *   colon form ("Prénom: x"), same-line bilingual (no colon), value on next line.
 */
const findFieldValue = (lines: string[], fieldKey: FieldKey): string | null => {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const inline = extractInlineValue(line, fieldKey);
    if (inline) return inline;

    const same = extractSameLineLabelValue(line, fieldKey);
    if (same) return same;

    if (isLabelOnly(line, fieldKey)) {
      for (let j = i + 1; j < Math.min(lines.length, i + 4); j++) {
        const next = lines[j].trim();
        if (!next) continue;
        if (NON_DATA_LINE.test(next) || isAnyLabel(next)) continue;
        return next;
      }
    }
  }
  return null;
};

// ============================================================================
// 5. DOCUMENT TYPE DETECTION
// ============================================================================

const detectDocumentType = (
  rawText: string,
  mrzLines: string[],
  hint?: string,
): 'passport' | 'identity_card' | 'driving_license' | 'other' => {
  // 1. MRZ-based detection (most reliable)
  if (mrzLines.some((l) => /^P<[A-Z0-9]{3}/.test(l))) return 'passport';
  if (mrzLines.some((l) => /^I[A-Z<]/.test(l) && l.length >= 27 && l.length <= 33)) return 'identity_card';
  if (mrzLines.some((l) => /^[ICA][A-Z<]/.test(l) && l.length >= 34 && l.length <= 38)) return 'identity_card';

  // 2. Header/keyword detection
  if (/\b(?:PASSPORT|PASSEPORT|PASAPORTE|REISEPASS|PASSAPORTO)\b/i.test(rawText)) {
    return 'passport';
  }
  if (/DRIV(?:ER|ING)['\s]+LICEN[CS]E|PERMIS\s+DE\s+CONDUIRE|F[ÜU]HRERSCHEIN|LICENCIA\s+DE\s+CONDUCIR|CARTA\s+DE\s+CONDU/i.test(rawText)) {
    return 'driving_license';
  }
  if (/IDENTITY\s+CARD|IDENTIFICATION\s+CARD|CARTE\s+(?:NATIONALE\s+D[''\u2019]?)?IDENTIT[ÉE]|PERSONALAUSWEIS|DOCUMENTO\s+NACIONAL\s+DE\s+IDENTIDAD|NATIONAL\s+IDENTITY/i.test(rawText)) {
    return 'identity_card';
  }
  if (looksLikeNorthAmericanNumberedId(rawText)) {
    return 'identity_card';
  }

  // 3. Hint fallback
  if (hint === 'passport' || hint === 'identity_card' || hint === 'driving_license') {
    return hint;
  }
  return 'other';
};

// ============================================================================
// 6. INTERNATIONAL TEXT-LABEL EXTRACTOR
// ============================================================================

const cleanName = (s: string): string =>
  s.replace(/[^A-Za-zÀ-ÿ\s'\-]/g, '').replace(/\s+/g, ' ').trim();

const cleanDocNumber = (s: string): string =>
  s.toUpperCase().replace(/[^A-Z0-9]/g, '').trim();

const LABEL_WORDS = new Set([
  'surname', 'nom', 'prenom', 'prénom', 'first', 'given', 'name', 'names',
  'nationality', 'nationalite', 'nationalité', 'sex', 'sexe',
  'date', 'birth', 'naissance', 'passport', 'document', 'code', 'country',
  'pays', 'type', 'expiry', 'expiration', 'issue', 'no', 'number', 'du', 'de', 'of',
]);

const isLikelyLabelOnlyText = (value: string): boolean => {
  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return true;
  const tokens = normalized.split(' ').filter(Boolean);
  if (!tokens.length) return true;
  return tokens.every((t) => LABEL_WORDS.has(t));
};

const looksLikeNorthAmericanNumberedId = (rawText: string): boolean => {
  return /\bIDENTIFICATION\s+CARD\b/i.test(rawText)
    || /\b(?:DOB|EXP|ISS|IDN)\s*:/i.test(rawText)
    || /\b4d\s+IDN\s*:/i.test(rawText)
    || (/\b1\s+[A-Z][A-Z\s'-]{2,}\b/.test(rawText) && /\b2\s+[A-Z][A-Z\s'-]{2,}\b/.test(rawText));
};

/** US state DLs often use LN/FN/DOB/EXP/DL abbreviations (no numbered EU fields). */
const looksLikeUsDriverLicenseAbbrev = (rawText: string): boolean =>
  /\bDRIVER\s+LICENSE\b/i.test(rawText)
  && /\bLN\s+[A-Z]/i.test(rawText)
  && /\bFN\s+/i.test(rawText)
  && /\bDOB\s+\d{1,2}\/\d{1,2}\/\d{4}/i.test(rawText);

/**
 * Parse US driver license OCR blocks: LN, FN, DOB, EXP, ISS, DL, optional address lines.
 */
const parseUsDriverLicenseAbbreviated = (rawText: string): Partial<ParsedIDData> => {
  const out: Partial<ParsedIDData> = {};
  const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);

  for (const line of lines) {
    const ln = line.match(/^LN\s+(.+)$/i);
    if (ln?.[1]) {
      const v = cleanName(ln[1]);
      if (v) out.nom = v.toUpperCase();
      continue;
    }
    const fn = line.match(/^FN\s+(.+)$/i);
    if (fn?.[1]) {
      const v = cleanName(fn[1]);
      if (v) out.prenom = v;
      continue;
    }
    const dob = line.match(/^DOB\s+(\d{1,2}\/\d{1,2}\/\d{4})\b/i);
    if (dob?.[1]) {
      const iso = parseUsDate(dob[1]);
      if (iso) out.date_naissance = iso;
      continue;
    }
    const exp = line.match(/^EXP\s+(\d{1,2}\/\d{1,2}\/\d{4})\b/i);
    if (exp?.[1]) {
      const iso = parseUsDate(exp[1]);
      if (iso) out.expiry_date = iso;
      continue;
    }
    const dl = line.match(/^DL\s+([A-Z0-9\s-]+)$/i);
    if (dl?.[1]) {
      const id = cleanDocNumber(dl[1]);
      if (id.length >= 5 && id.length <= 20) out.numero_id = id;
    }
  }

  if (/\bUSA\b|\bUNITED\s+STATES\b/i.test(rawText)) {
    out.nationality = 'USA';
  }

  // Optional: city, ST ZIP line after street (not true birthplace on many US DLs)
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\d+\s+.+\s+(?:ST|STREET|RD|AVE|BLVD|DR)\b/i.test(line)) {
      const next = lines[i + 1] || '';
      if (/^[A-Z][A-Za-z\s]+,\s*[A-Z]{2}\s+\d{5}/.test(next)) {
        out.lieu_naissance = `${line}, ${next}`.trim();
        break;
      }
    }
  }

  return out;
};

/**
 * Parse UK-style driving licence numbered fields, including OCR outputs that
 * come as markdown-like tables:
 *   | 1. | CAMPBELL |
 *   | 2. | ANDREA |
 *   | 3. | JOAN |
 *   | 4a.| 05.07.1964 BELFAST |
 *   | 5. | 30.11.2031 |
 *   | 6. | 99999999 |
 */
const parseDrivingLicenceNumberedLayout = (rawText: string): Partial<ParsedIDData> => {
  const out: Partial<ParsedIDData> = {};
  const rows = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  const fields: Record<string, string> = {};

  for (const row of rows) {
    // Markdown table cell form: "| 4a. | 05.07.1964 BELFAST |"
    const tableMatch = row.match(/^\|\s*([0-9]{1,2}[a-z]?)\.?\s*\|\s*(.*?)\s*\|?$/i);
    if (tableMatch) {
      fields[tableMatch[1].toLowerCase()] = tableMatch[2].trim();
      continue;
    }
    // Plain text form: "4a. 05.07.1964 BELFAST"
    const plainMatch = row.match(/^([0-9]{1,2}[a-z]?)\.?\s+(.+)$/i);
    if (plainMatch) {
      fields[plainMatch[1].toLowerCase()] = plainMatch[2].trim();
    }
  }

  // Surname (1)
  if (fields['1']) {
    const v = cleanName(fields['1']);
    if (v) out.nom = v.toUpperCase();
  }

  // Given names (2 + 3) if present
  // Some countries use 3 for DOB/location; only append field 3 if it's truly name-like.
  const g2 = fields['2'] ? cleanName(fields['2']) : '';
  const g3Raw = fields['3'] || '';
  const g3 = (g3Raw && !/\d/.test(g3Raw) && !/\b(?:DOB|DATE|NAISS|BIRTH)\b/i.test(g3Raw))
    ? cleanName(g3Raw)
    : '';
  const given = [g2, g3].filter(Boolean).join(' ').trim();
  if (given) out.prenom = given;

  // Prefer 3 for DOB on EU/FR cards; fallback to 4a if needed (UK-like OCR layouts).
  if (fields['3']) {
    const v = fields['3'];
    const dob = parseDate(v);
    if (dob) out.date_naissance = dob;
    // If DOB is present, trailing text is likely place of birth.
    const place = v
      .replace(/\b\d{1,2}[\/.\-\s]\d{1,2}[\/.\-\s]\d{4}\b/, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (place && !/^[0-9.\/-]+$/.test(place)) out.lieu_naissance = place;
  }
  if (!out.date_naissance && fields['4a']) {
    const dob = parseDate(fields['4a']);
    if (dob) out.date_naissance = dob;
  }

  // Expiry: EU/FR often in 4b, UK OCR sample sometimes in 5.
  if (fields['4b']) {
    const exp = parseDate(fields['4b']);
    if (exp) out.expiry_date = exp;
  }
  if (!out.expiry_date && fields['5']) {
    const exp = parseDate(fields['5']);
    if (exp) out.expiry_date = exp;
  }

  // Document/licence number: EU/FR often in 5; fallback to 6 if it looks like an id.
  if (fields['5']) {
    const id = cleanDocNumber(fields['5']);
    if (id.length >= 5 && id.length <= 20) out.numero_id = id;
  }
  if (!out.numero_id && fields['6']) {
    const id = cleanDocNumber(fields['6']);
    if (id.length >= 5 && id.length <= 20 && !/^SIGNATURE$/i.test(id)) out.numero_id = id;
  }

  // Nationality fallback from header if present
  if (/\bUK\b|\bUNITED\s+KINGDOM\b/i.test(rawText)) {
    out.nationality = out.nationality || 'GBR';
  }

  return out;
};

/**
 * Parse North-American numbered ID/DL formats (plain OCR lines), e.g.:
 *   1 SAMPLE
 *   2 ANDREW JASON
 *   3 DOB: 01/07/1973
 *   4b EXP: 01/31/2026
 *   4a ISS: 01/07/2022
 *   4d IDN: 99 999 999
 *   5 DD:1234567890123
 */
const parseNorthAmericanNumberedIdLayout = (rawText: string): Partial<ParsedIDData> => {
  const out: Partial<ParsedIDData> = {};
  const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  const fields: Record<string, string> = {};

  for (const line of lines) {
    const indexed = line.match(/^([0-9]{1,2}[a-z]?)\.?\s*(.+)$/i);
    if (indexed) {
      const key = indexed[1].toLowerCase();
      const value = indexed[2].trim();
      if (value) fields[key] = value;
    }

    if (!fields['3']) {
      const dob = line.match(/\bDOB\s*:\s*([0-9./-]{8,10})\b/i);
      if (dob?.[1]) fields['3'] = dob[1];
    }
    if (!fields['4b']) {
      const exp = line.match(/\bEXP\s*:\s*([0-9./-]{8,10})\b/i);
      if (exp?.[1]) fields['4b'] = exp[1];
    }
    if (!fields['4d']) {
      const idn = line.match(/\bIDN\s*:\s*([A-Z0-9\s-]{5,30})$/i);
      if (idn?.[1]) fields['4d'] = idn[1].trim();
    }
    if (!fields['5']) {
      const dd = line.match(/\bDD\s*:\s*([A-Z0-9\s-]{6,30})$/i);
      if (dd?.[1]) fields['5'] = dd[1].trim();
    }
  }

  if (fields['1']) {
    const nom = cleanName(fields['1']);
    if (nom) out.nom = nom.toUpperCase();
  }
  if (fields['2']) {
    const prenom = cleanName(fields['2']);
    if (prenom) out.prenom = prenom;
  }

  if (fields['3']) {
    const dob = parseDate(fields['3']);
    if (dob) out.date_naissance = dob;
  }
  if (fields['4b']) {
    const exp = parseDate(fields['4b']);
    if (exp) out.expiry_date = exp;
  }

  // Prefer explicit IDN over DD
  if (fields['4d']) {
    const idn = cleanDocNumber(fields['4d']);
    if (idn.length >= 6 && idn.length <= 20) out.numero_id = idn;
  }
  if (!out.numero_id && fields['5']) {
    const dd = cleanDocNumber(fields['5']);
    if (dd.length >= 6 && dd.length <= 20) out.numero_id = dd;
  }

  if (/\bUSA\b|\bUNITED\s+STATES\b|\bPENNSYLVANIA\b/i.test(rawText)) {
    out.nationality = 'USA';
  }

  return out;
};

const extractPassportBirthPlaceFallback = (rawText: string): string => {
  // OCR sometimes misreads "Lieu de naissance" as "Code postal" on French passports.
  const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/(?:LIEU\s+DE\s+NAISSANCE|PLACE\s+OF\s+BIRTH|CODE\s+POSTAL)/i.test(line)) {
      // Try inline value after a separator first.
      const inline = line.replace(/.*(?:LIEU\s+DE\s+NAISSANCE|PLACE\s+OF\s+BIRTH|CODE\s+POSTAL)\s*[:/\\-]?\s*/i, '').trim();
      if (inline && !isLikelyLabelOnlyText(inline) && !/\b(?:LIEU|PLACE|CODE|POSTAL)\b/i.test(inline)) {
        return inline;
      }
      // Else try next non-empty non-label line.
      const next = lines[i + 1] || '';
      if (next && !isLikelyLabelOnlyText(next) && !/(?:DATE|SEXE|NATIONALIT|PASSPORT|NOM|PRENOM)/i.test(next)) {
        return next;
      }
    }
  }
  return '';
};

/** Run all label-based extractors on the OCR text. Used by every doc type. */
const extractAllLabeledFields = (rawText: string): Partial<ParsedIDData> => {
  const lines = rawText.split('\n').map((l) => l.trim()).filter((l) => l !== '');
  const out: Partial<ParsedIDData> = {};

  // Surname
  const surname = findFieldValue(lines, 'surname');
  if (surname) {
    const v = cleanName(surname);
    if (v.length >= 2 && v.length <= 60) out.nom = v.toUpperCase();
  }

  // Given names
  const given = findFieldValue(lines, 'given_names');
  if (given) {
    const v = cleanName(given.replace(/,/g, ' '));
    if (v.length >= 1 && v.length <= 80) out.prenom = v;
  }

  // Date of birth
  const dob = findFieldValue(lines, 'date_of_birth');
  if (dob) {
    const iso = parseDate(dob);
    if (iso) out.date_naissance = iso;
  }

  // Place of birth
  const pob = findFieldValue(lines, 'place_of_birth');
  if (pob && pob.length >= 2 && pob.length < 120) {
    out.lieu_naissance = pob.trim();
  }

  // Expiry date
  const expiry = findFieldValue(lines, 'expiry_date');
  if (expiry) {
    const iso = parseDate(expiry);
    if (iso) out.expiry_date = iso;
  }

  // Nationality
  const nat = findFieldValue(lines, 'nationality');
  if (nat) {
    out.nationality = normalizeNationality(nat);
  }
  if (!out.nationality) {
    // Defensive fallback for bilingual CIN lines when label parsing is noisy.
    const upper = rawText.toUpperCase();
    const m = upper.match(/(?:NATIONALIT[ÉE]|NATIONALITY)[^\nA-Z0-9]*([A-Z]{3})\b/);
    if (m?.[1]) out.nationality = m[1];
  }

  // Document number — labeled
  const docNum = findFieldValue(lines, 'document_number');
  if (docNum) {
    const cleaned = cleanDocNumber(docNum);
    if (
      cleaned.length >= 5
      && cleaned.length <= 20
      && !/^PASSPORTNO(?:NUMBER)?$/i.test(cleaned)
      && !/^DOCUMENTNO(?:NUMBER)?$/i.test(cleaned)
    ) {
      out.numero_id = cleaned;
    }
  }

  // Document number — fallback: standalone numeric/alnum line
  if (!out.numero_id) {
    for (const line of lines) {
      if (/^DL\s+/i.test(line) || /^DD\s+/i.test(line)) continue;
      const cleaned = line.trim().replace(/\s/g, '');
      // 8-15 digits alone (UK ID, US passport No.)
      if (/^\d{8,15}$/.test(cleaned)) { out.numero_id = cleaned; break; }
      // Alphanumeric 7-15, must have both letters and digits (FR CIN, FR passport)
      if (/^[A-Z][A-Z0-9]{6,14}$/.test(cleaned) && /\d/.test(cleaned) && !/^\d/.test(cleaned)) {
        out.numero_id = cleaned;
        break;
      }
    }
  }

  return out;
};

// ============================================================================
// 7. MAIN PARSER ENTRY
// ============================================================================

/** Merge two field maps; primary wins when its value is non-empty. */
const mergeFields = (
  primary: Partial<ParsedIDData>,
  secondary: Partial<ParsedIDData>,
): Partial<ParsedIDData> => {
  const result: Partial<ParsedIDData> = { ...secondary };
  for (const key of Object.keys(primary) as (keyof ParsedIDData)[]) {
    const val = primary[key];
    if (val && String(val).trim()) {
      (result as any)[key] = val;
    }
  }
  return result;
};

/**
 * Parse OCR text from any identity document.
 *
 * Strategy (the same flow handles every supported doc type):
 *   1. Detect doc type (MRZ → keywords → hint)
 *   2. Run MRZ parser if applicable (TD3 / TD2 / TD1)
 *   3. Run international label parser on the printed text
 *   4. Merge — printed text wins when present (it's human-readable),
 *      MRZ fills missing fields.
 */
export const parseDocumentOCR = (rawText: string, docTypeHint?: string): ParsedIDData => {
  const empty: ParsedIDData = {
    nom: '', prenom: '', date_naissance: '', lieu_naissance: '',
    numero_id: '', nationality: '', expiry_date: '', detected_doc_type: '',
  };

  console.group('%c[OCR Parser] parseDocumentOCR', 'color:#1565c0;font-weight:bold');
  console.log('docTypeHint:', docTypeHint ?? '(none)');
  console.log('--- RAW OCR TEXT (start) ---');
  console.log(rawText);
  console.log('--- RAW OCR TEXT (end) ---');

  if (!rawText) {
    console.warn('[OCR Parser] empty input');
    console.groupEnd();
    return empty;
  }

  const mrzLines = findMrzLines(rawText);
  console.log('[OCR Parser] MRZ candidate lines:', mrzLines.length, mrzLines);

  let docType = detectDocumentType(rawText, mrzLines, docTypeHint);
  console.log('[OCR Parser] Detected doc type:', docType);

  // ── MRZ extraction ────────────────────────────────────────────────────────
  let mrzResult: Partial<ParsedIDData> = {};
  const mrzLibraryResult = parseWithMrzLibrary(mrzLines);
  if (Object.keys(mrzLibraryResult).length > 0) {
    console.log('[OCR Parser] MRZ library fields:', mrzLibraryResult);
    mrzResult = mergeFields(mrzLibraryResult, mrzResult);
    if (mrzLibraryResult.detected_doc_type) {
      docType = mrzLibraryResult.detected_doc_type;
      console.log('[OCR Parser] Doc type overridden by MRZ library:', docType);
    }
  }

  if (docType === 'passport') {
    const passLine1 = mrzLines.find((l) => /^P<[A-Z0-9]{3}/.test(l) && l.length >= 30);
    if (passLine1) {
      const idx = mrzLines.indexOf(passLine1);
      const next = mrzLines[idx + 1];
      if (next && (/[0-9]{6}[MF<][0-9]{6}/.test(next) || /^[A-Z0-9<]{30,}$/.test(next))) {
        console.log('[OCR Parser] ✅ TD3 passport MRZ');
        console.log('  line1:', passLine1);
        console.log('  line2:', next);
        mrzResult = parseTD3(passLine1, next);
      }
    }
  } else if (docType === 'identity_card') {
    // TD1: 3 × 30
    const shortLines = mrzLines.filter((l) => l.length >= 27 && l.length <= 33);
    const td1Line1 = shortLines.find((l) => /^I[A-Z<]/.test(l));
    if (td1Line1) {
      const idx = shortLines.indexOf(td1Line1);
      const l2 = shortLines[idx + 1];
      const l3 = shortLines[idx + 2];
      if (l2 && l3) {
        console.log('[OCR Parser] ✅ TD1 ID MRZ');
        mrzResult = parseTD1(td1Line1, l2, l3);
      }
    }
    // TD2: 2 × 36
    if (!mrzResult.nom) {
      const medLines = mrzLines.filter((l) => l.length >= 34 && l.length <= 38);
      const td2Line1 = medLines.find((l) => /^[ICA][A-Z<]/.test(l));
      if (td2Line1) {
        const idx = medLines.indexOf(td2Line1);
        const l2 = medLines[idx + 1];
        if (l2) {
          console.log('[OCR Parser] ✅ TD2 MRZ');
          mrzResult = parseTD2(td2Line1, l2);
        }
      }
    }
  }

  // ── Printed-text (visual zone) extraction ─────────────────────────────────
  const textResult = extractAllLabeledFields(rawText);
  const drivingLayoutResult = docType === 'driving_license'
    ? parseDrivingLicenceNumberedLayout(rawText)
    : {};
  const northAmericaLayoutResult =
    ((docType === 'identity_card' || docType === 'driving_license' || docType === 'other')
      && looksLikeNorthAmericanNumberedId(rawText))
      ? parseNorthAmericanNumberedIdLayout(rawText)
      : {};
  const usDlAbbrevResult =
    docType === 'driving_license' && looksLikeUsDriverLicenseAbbrev(rawText)
      ? parseUsDriverLicenseAbbreviated(rawText)
      : {};
  const combinedTextResult = mergeFields(
    usDlAbbrevResult,
    mergeFields(mergeFields(textResult, drivingLayoutResult), northAmericaLayoutResult),
  );
  console.log('[OCR Parser] Text-label fields:', textResult);
  if (docType === 'driving_license') {
    console.log('[OCR Parser] Driving layout fields:', drivingLayoutResult);
  }
  if (Object.keys(northAmericaLayoutResult).length > 0) {
    console.log('[OCR Parser] North-America layout fields:', northAmericaLayoutResult);
  }
  if (Object.keys(usDlAbbrevResult).length > 0) {
    console.log('[OCR Parser] US DL abbrev fields:', usDlAbbrevResult);
  }

  // ── Merge strategy ─────────────────────────────────────────────────────────
  // For passports, MRZ is usually the most reliable source for names/number.
  // For non-passport documents, keep text-first behavior.
  const merged = docType === 'passport'
    ? mergeFields(mrzResult, combinedTextResult)
    : mergeFields(combinedTextResult, mrzResult);

  if (docType === 'passport' && !merged.lieu_naissance) {
    const passportBirthPlace = extractPassportBirthPlaceFallback(rawText);
    if (passportBirthPlace) {
      merged.lieu_naissance = passportBirthPlace;
    }
  }
  merged.detected_doc_type = docType;

  const result = { ...empty, ...merged } as ParsedIDData;
  console.log('%c[OCR Parser] ✔ Final result', 'color:#2e7d32;font-weight:bold', result);
  console.groupEnd();
  return result;
};

// ============================================================================
// 8. OCR.SPACE — DOCUMENT SCANNING (via backend proxy)
// ============================================================================

/**
 * Document OCR — calls the backend proxy `/api/ocr/document`.
 * Tries Engine 3 → 2 → 1 across French/English to pick the strongest output.
 */
export const ocrSpaceDocumentAPI = async (
  imageBase64: string,
): Promise<{ text: string; confidence: number }> => {
  if (!OCR_SPACE_API_KEY) {
    // Backend key is authoritative. Frontend env var is optional (legacy).
    console.warn('REACT_APP_OCR_SPACE_API_KEY is not set in frontend; using backend OCR proxy key.');
  }

  const scoreText = (text: string): number => {
    if (!text.trim()) return 0;
    let score = 0;
    if (/[A-Z0-9<]{25,}/.test(text.replace(/\s/g, ''))) score += 2; // MRZ-like density
    if (/PASSPORT|IDENTITY|CARTE|DOCUMENT|LICEN[CS]E|PERMIS/i.test(text)) score += 2;
    if (/\b\d{1,2}[\/.\-\s]\d{2}[\/.\-\s]?\d{4}\b/.test(text)) score += 1;
    if (text.length > 120) score += 1;
    return score;
  };

  const callDocumentEngine = async (
    engine: '1' | '2' | '3',
    language: 'eng' | 'fre',
  ): Promise<{ text: string; confidence: number }> => {
    console.group(`%c[OCR] ocrSpaceDocumentAPI (Engine ${engine} / ${language})`, 'color:#7b1fa2;font-weight:bold');
    console.log(`Sending image to backend OCR proxy — Engine ${engine}, language=${language}`);

    const response = await api.post('/ocr/document', {
      base64Image: imageBase64,
      language,
      engine,
    });
    const data = response.data?.raw || {};
    console.log('[OCR] Full OCR.space response:', JSON.stringify(data, null, 2));

    if (data.ParsedResults?.length > 0) {
      const r = data.ParsedResults[0];
      const text = r.ParsedText || '';
      const confidence = r.MeanConfidenceLevel ? Math.round(r.MeanConfidenceLevel * 100) : 70;
      if (text.trim()) {
        console.log(`[OCR] Engine ${engine} success — confidence: ${confidence}%`);
        console.log(`[OCR] Text score: ${scoreText(text)}`);
        console.log('[OCR] ParsedText:\n' + text);
        console.groupEnd();
        return { text, confidence };
      }
    }
    console.warn(`[OCR] Engine ${engine} returned no usable text`);
    console.groupEnd();
    return { text: '', confidence: 0 };
  };

  const attempts: Array<{ engine: '1' | '2' | '3'; language: 'eng' | 'fre' }> = [
    { engine: '3', language: 'eng' },
    { engine: '3', language: 'fre' },
    { engine: '2', language: 'eng' },
    { engine: '2', language: 'fre' },
    { engine: '1', language: 'eng' },
    { engine: '1', language: 'fre' },
  ];

  let best = { text: '', confidence: 0, score: 0, engine: '', language: '' };
  for (const a of attempts) {
    const res = await callDocumentEngine(a.engine, a.language);
    const s = scoreText(res.text);
    if (s > best.score || (s === best.score && res.text.length > best.text.length)) {
      best = { ...res, score: s, engine: a.engine, language: a.language };
    }
    if (s >= 4) break; // Strong enough — skip remaining engines
  }

  if (best.text.trim()) {
    console.log(`[OCR] Selected best OCR output from Engine ${best.engine}/${best.language} (score=${best.score})`);
    return { text: best.text, confidence: best.confidence };
  }
  return { text: '', confidence: 0 };
};

// ============================================================================
// 9. OCR.SPACE — NUMBER PLATE / GENERAL IMAGE SCANNING
// ============================================================================

/** Lightweight OCR call for non-document use cases (license plates, etc.). */
export const ocrSpaceAPI = async (imageBase64: string): Promise<{ text: string; confidence: number }> => {
  console.group('%c[OCR] ocrSpaceAPI (Engine 3 / eng — general/plates)', 'color:#e65100;font-weight:bold');

  const response = await api.post('/ocr/document', {
    base64Image: imageBase64,
    language: 'eng',
    engine: '3',
  });
  const data = response.data?.raw || {};
  console.log('[OCR] Engine 3 full response:', JSON.stringify(data, null, 2));

  if (data.ParsedResults?.length > 0) {
    const r = data.ParsedResults[0];
    const text = r.ParsedText || '';
    console.log('[OCR] Engine 3 ParsedText:\n' + text);
    console.groupEnd();
    return {
      text,
      confidence: r.MeanConfidenceLevel ? Math.round(r.MeanConfidenceLevel * 100) : 70,
    };
  }

  console.warn('[OCR] Engine 3 returned no ParsedResults');
  console.groupEnd();
  return { text: '', confidence: 0 };
};

// ============================================================================
// 10. GOOGLE CLOUD VISION (legacy / optional)
// ============================================================================

export const googleVisionAPI = async (
  imageBase64: string,
  apiKey: string,
): Promise<{ text: string; confidence: number }> => {
  const response = await fetch(
    `https://vision.googleapis.com/v1/images:annotate?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        requests: [{
          image: { content: imageBase64.split(',')[1] },
          features: [{ type: 'TEXT_DETECTION', maxResults: 1 }],
        }],
      }),
    },
  );
  const data = await response.json();
  if (data.responses?.[0]?.textAnnotations?.length > 0) {
    const ann = data.responses[0].textAnnotations[0];
    return { text: ann.description || '', confidence: Math.round((ann.confidence || 0.9) * 100) };
  }
  return { text: '', confidence: 0 };
};
